/**
 * Auth Session Guard
 * Prevents multiple OAuth sessions from starting simultaneously
 * Singleton pattern ensures only one auth session is active at a time
 */

class AuthSessionGuard {
	private static instance: AuthSessionGuard;
	private activeSession: string | null = null;
	private sessionPromise: Promise<any> | null = null;

	private constructor() {
		// Private constructor for singleton
	}

	public static getInstance(): AuthSessionGuard {
		if (!AuthSessionGuard.instance) {
			AuthSessionGuard.instance = new AuthSessionGuard();
		}
		return AuthSessionGuard.instance;
	}

	/**
	 * Check if an auth session is currently active
	 */
	public isSessionActive(): boolean {
		return this.activeSession !== null;
	}

	/**
	 * Get the currently active session type
	 */
	public getActiveSession(): string | null {
		return this.activeSession;
	}

	/**
	 * Start a new auth session
	 * Returns false if a session is already active
	 */
	public async startSession(
		sessionType: string,
		promptFn: () => Promise<any>
	): Promise<any> {
		// Check if another session is active
		if (this.activeSession && this.activeSession !== sessionType) {
			console.warn(
				`[AuthSessionGuard] Cannot start ${sessionType} - ${this.activeSession} is already active`
			);
			return {
				type: 'error',
				error: `Another authentication session (${this.activeSession}) is already in progress. Please wait.`,
			};
		}

		// If same session type is clicked again while active, return the existing promise
		if (this.activeSession === sessionType && this.sessionPromise) {
			console.log(
				`[AuthSessionGuard] ${sessionType} already active, returning existing promise`
			);
			return this.sessionPromise;
		}

		try {
			console.log(`[AuthSessionGuard] Starting ${sessionType} session`);
			this.activeSession = sessionType;

			// Store the promise so concurrent calls can wait for it
			this.sessionPromise = promptFn();
			const result = await this.sessionPromise;

			console.log(`[AuthSessionGuard] ${sessionType} session completed`);
			return result;
		} catch (error) {
			console.error(
				`[AuthSessionGuard] ${sessionType} session error:`,
				error
			);
			throw error;
		} finally {
			// Clear session state
			this.activeSession = null;
			this.sessionPromise = null;
		}
	}

	/**
	 * Clear the active session (for cleanup/cancel)
	 */
	public clearSession(): void {
		console.log(
			`[AuthSessionGuard] Clearing session: ${this.activeSession}`
		);
		this.activeSession = null;
		this.sessionPromise = null;
	}

	/**
	 * Reset the guard (for testing)
	 */
	public static reset(): void {
		if (AuthSessionGuard.instance) {
			AuthSessionGuard.instance.clearSession();
		}
	}
}

// Export singleton instance getter
export const getAuthSessionGuard = () => AuthSessionGuard.getInstance();

export default AuthSessionGuard;
